// Bug entities and their lifecycle state machine (Unity `Bug.cs` and the
// per-bug state handling in `BugsModel.cs`; see `unity-overview.md` §3
// "Bug types"). Pure logic: no WebGL, no DOM — the renderer reads this state
// and the view subscribes to the bus for spawn/state/removal notifications.

import type { EventBus } from "./events";
import type { BlocksModel, Cell } from "./blocks";
import type { Rng } from "./rng";
import {
  advance,
  distanceToNextTile,
  frameAtHeading,
  poseToCell,
  poseToWorld,
  turnLeft,
  turnRight,
  DIR_UV,
  N,
  type Dir4,
  type FaceId,
  type SurfacePose,
  type Vec3,
} from "./surface";
import { bodyTiles, Occupancy, restingTile, tileKey, type TileKey } from "./occupancy";
import {
  ACCELERATION,
  BLACK_DESPAWN_SECONDS,
  BLOCKED_SECONDS_BASE,
  BUG_DEFS,
  DIE_SECONDS,
  EIGHT_STAR,
  FAT_HITS,
  FAT_STAR_INFECTS,
  HALF_BODY,
  INFECT_TILES_MAX,
  INFECT_TILES_MIN,
  BITE_SECONDS,
  LIFETIME_MULTIPLIERS,
  PICK_SECONDS,
  SPAWN_SECONDS,
  SPY_MARKS_TO_INFECT,
  STOPPED_SPEED,
  TURN_DEGREES_PER_SPEED,
} from "./defs";
import type { LevelSource } from "./levels";
import { createModifiers, type GlobalModifiers } from "./modifiers";

/**
 * What the movement system needs from the power-up system: the trample hook.
 * `PowerUpManager` implements it; declared here (rather than importing it) so
 * the bugs -> powerups dependency stays a one-way type-only edge — the
 * kill-half effect already needs powerups -> bugs.
 */
export interface PowerUpSink {
  killAt(cell: Cell): void;
}

/** Branded entity id (00-architecture.md §6). */
export type BugId = number & { readonly __brand: "BugId" };

/** The seven bug kinds, in `BugsDefinitionInspector` order. */
export type BugKind = "standard" | "lady" | "fat" | "speed" | "spy" | "spike" | "black";

/**
 * Lifecycle as a discriminated union (00-architecture.md §5). `progress` is
 * normalised to [0, 1] over the state's duration, so the renderer can drive
 * both a grow-in scale and a clip time from it without knowing durations.
 */
export type BugState =
  | { kind: "spawning"; progress: number } // grow-in over SPAWN_SECONDS
  | { kind: "running" }
  | { kind: "infecting"; progress: number } // entered by plan 07
  | { kind: "dying"; progress: number }
  | { kind: "takenByPlayer"; progress: number }; // lady/black pickup (plan 08)

/**
 * A quarter-circle turn in face-local coordinates, mirroring the original's
 * `TurnCorner`: the walker pivots around the corner point half a cell to its
 * side, so its path is an arc of radius 0.5 cells rather than a kink.
 * `pos(a) = pivot + s·cos a + e·sin a`, with `s` pointing from the pivot back
 * to the entry point and `e` (perpendicular, same length) along the entry
 * heading — so the tangent runs from `e` to `-s` as `a` sweeps 0 → 90°.
 */
interface TurnArc {
  readonly face: FaceId;
  readonly pivot: readonly [number, number];
  readonly s: readonly [number, number];
  readonly e: readonly [number, number];
  readonly toDir: Dir4;
  /** Radians swept so far, 0 … π/2. */
  angle: number;
}

/**
 * How a *running* bug is moving right now. This is the original's `State`
 * (Walking / TurnCorner) split off from the lifecycle above, which it is
 * orthogonal to: a turning bug still renders its run cycle and can still be
 * killed. Braking and waiting need no state of their own — `speed` carries it.
 */
export type Motion = { kind: "cruising" } | { kind: "turning"; arc: TurnArc };

export interface Bug {
  readonly id: BugId;
  readonly kindDef: BugKind;
  pose: SurfacePose;
  state: BugState;
  motion: Motion;
  /** Current walking speed in cells/s; ramps toward the kind's `speed`. */
  speed: number;
  /** Seconds spent alive and active — running or infecting (scoring, plan 08+). */
  lifeTime: number;
  /** Seconds spent stopped by an obstruction; resets as soon as it moves. */
  blockedFor: number;
  /**
   * Tiles still to walk before the next bite — the original's `DistanceLeft`,
   * counted in tiles rather than world units. Set it to `Infinity` to park a
   * walker's appetite (tests that measure pure movement do).
   */
  tilesUntilInfect: number;
  /** Fat bug needs three taps (plan 08). */
  hitsTaken: number;
  /**
   * Blocks a **spy** has walked over and claimed — the original's
   * `Bug.MarkedBlocks`. Empty for every other kind. At `SPY_MARKS_TO_INFECT`
   * entries the spy stops and infects all of them at once; any kill forfeits
   * them (`KillBug`, `BugsModel.cs:2804`).
   *
   * SIMPLIFY: the original keys a mark on `(BlockId, Plane)` and dedups on the
   * pair while counting distinct `BlockId`s for the threshold. This port has no
   * per-bug plane concept, so a mark is just the cell and both jobs use it.
   */
  readonly markedBlocks: Cell[];
}

/**
 * Summing `dt / duration` over exactly `duration` seconds of fixed steps lands
 * a hair under 1 in floating point (60 x 1/60 = 0.9999999999999999), which
 * would stretch every timed state by one extra step. Close the gap explicitly.
 */
const PROGRESS_EPSILON = 1e-9;

/** What the manager must announce after stepping one bug. */
type StepResult = "none" | "changed" | "removed";

// ---------------------------------------------------------------------------
// Collision, speed and turning (plan 05)
// ---------------------------------------------------------------------------

/** Distance in cells scanned ahead for walkers in the way. */
const LOOK_AHEAD = 2;

/** Slack for comparing distances against tile boundaries, in cells. */
const EPS = 1e-9;

/**
 * A walker stops this far short of the tile it may not enter. Coming to rest
 * with the leading edge *exactly* on the boundary would bin that edge into the
 * forbidden tile — `floor(3.0)` is 3 — and the claim would be refused after the
 * brake had already done its job.
 */
const BOUNDARY_SLACK = 1e-6;

const QUARTER_TURN = Math.PI / 2;
const TURN_RADIANS_PER_SPEED = (TURN_DEGREES_PER_SPEED * Math.PI) / 180;

/**
 * What a walker needs to know about the rest of the world to take a step: who
 * holds which tile, who those holders are, and where the surface still exists.
 */
interface Surroundings {
  readonly occupancy: Occupancy;
  readonly bugs: ReadonlyMap<BugId, Bug>;
  readonly blocks: BlocksModel;
  /** The sim's seeded RNG — the infect coin flip and the fat star draw on it. */
  readonly rng: Rng;
  /**
   * The level's `SpeedFactor` (plan 15). CORRECTION: the original multiplies
   * *travel per frame* and leaves `CurrentSpeed` alone, which desynchronises
   * the brake — `targetSpeed` solves `v = sqrt(2·a·gap)` in unscaled units
   * while the walker actually covers `v · factor · dt`, so at level 30's 1.16
   * a bug overshoots its stopping distance by 16% and noses into whatever it
   * was braking for. This port scales the **top speed** instead, so travel,
   * braking and the turn rate all stay in agreement (they already all derive
   * from `bug.speed`). The only observable difference is that the acceleration
   * ramp reaches the higher top speed slightly later — sub-frame at ≤1.16×.
   */
  readonly speedFactor: number;
  /** The power-up effects' global fields — the freeze read lives here (plan 16). */
  readonly modifiers: GlobalModifiers;
  /** The power-ups to trample on tile entry; null until plan 16's wiring. */
  readonly powerups: PowerUpSink | null;
  /** Tiles to walk before the next bite — a fresh draw per bite. */
  nextBiteDistance(): number;
  /** Announce a spy's `markedBlocks` after the caller has mutated it. */
  marksChanged(bug: Bug): void;
}

/**
 * Why a walker may not enter a tile: someone else holds it, or the block behind
 * it has been eaten away and there is no surface left to walk on.
 *
 * The tile the walker currently stands on is always passable to it — a bug that
 * has just eaten the block out from under itself must be able to step off the
 * hole it made rather than brake against it and starve to death.
 *
 * "No surface" is `!isWalkable`, which rejects only `empty` — the original's
 * `CheckPositionIsWalkable2` (`BugsModel.cs:2278-2295`). `healthy`, `infected`
 * and `ghost` are all walkable, so a bug crosses the translucent shell an eaten
 * block leaves without deflecting. Plan 07 had this backwards and treated every
 * destroyed block as an obstacle; plan 12 corrected it.
 *
 * Nothing produces `empty` until plan 13 lands slice collapse, so in practice
 * only occupancy blocks a walker today. The surface test stays because it is
 * the correct rule, not because it currently fires.
 */
function isBlocked(world: Surroundings, bug: Bug, pose: SurfacePose, standing: TileKey): boolean {
  const key = tileKey(pose.face, pose.u, pose.v);
  const holder = world.occupancy.holder(key);
  if (holder !== null && holder !== bug.id) return true;
  if (key === standing) return false;
  const cell = poseToCell(pose);
  return !world.blocks.isWalkable(cell.x, cell.y, cell.z);
}

/**
 * How far the walker may still advance before its leading edge would enter a
 * tile it may not have; `null` when the way is clear for `LOOK_AHEAD` cells.
 * This is the distance the brake has to work with, so it is measured from the
 * leading edge rather than the body centre.
 */
function distanceToBlocker(world: Surroundings, bug: Bug, from: SurfacePose): number | null {
  const standing = tileKey(from.face, from.u, from.v);
  let probe = advance(from, HALF_BODY);
  // The nose normally sits in a tile the walker holds itself; when it does not
  // — a fence dropped in front of it, a neighbour that turned into the lane —
  // there is no room left at all.
  if (isBlocked(world, bug, probe, standing)) return 0;
  let travelled = 0;
  while (travelled < LOOK_AHEAD) {
    const step = distanceToNextTile(probe);
    probe = advance(probe, step + EPS);
    travelled += step;
    if (isBlocked(world, bug, probe, standing)) return travelled;
  }
  return null;
}

/**
 * Speed a walker may carry and still stop within `gap` at `ACCELERATION`
 * (`v = sqrt(2·a·d)`), capped by the kind's top speed. Braking then falls out
 * of the same ramp that accelerates it, so an approach reads as a slowdown and
 * the walker comes to rest exactly at the obstruction — the smooth-stop half of
 * the original's `BrakeToStopInDistance`, without its frame-rate-dependent
 * fudge factors.
 */
function targetSpeed(maxSpeed: number, gap: number | null): number {
  if (gap === null) return maxSpeed;
  return Math.min(maxSpeed, Math.sqrt(2 * ACCELERATION * Math.max(gap, 0)));
}

/** Move `value` toward `goal` by at most `step`. */
function approach(value: number, goal: number, step: number): number {
  if (value < goal) return Math.min(goal, value + step);
  return Math.max(goal, value - step);
}

/** Tile centre one cell along `(du, dv)` from a pose sitting on a boundary. */
function sideCentre(
  pose: SurfacePose,
  forward: readonly [number, number],
  side: readonly [number, number],
): { u: number; v: number } {
  return {
    u: pose.u + forward[0] * 0.5 + side[0],
    v: pose.v + forward[1] * 0.5 + side[1],
  };
}

/** True while a face-local point is on the face rather than past one of its edges. */
function onFace(u: number, v: number): boolean {
  return u >= 0 && u <= N && v >= 0 && v <= N;
}

/**
 * Decide, standing on a tile boundary, whether to turn aside — the original's
 * `CalculateNextActionOnEnterNewBlock`, which looks a block and a half ahead on
 * entering a new block and turns into a free neighbour rather than walking into
 * an occupant. Turning back is never an option; a walker with nowhere to go
 * brakes to a stop instead and waits for the way to clear.
 *
 * The horizon is deliberately the same `LOOK_AHEAD` the brake uses. Boundaries
 * are the only places a turn may start (an arc that starts anywhere else lands
 * off the lane centres), so a shorter horizon lets a walker brake to a halt
 * mid-tile and sit there with no decision point left to reach.
 *
 * APPROX: the original picks randomly when both sides are free; right-first
 * keeps the sim deterministic (00-architecture.md §3).
 */
function planTurn(world: Surroundings, bug: Bug, pose: SurfacePose): TurnArc | null {
  if (distanceToBlocker(world, bug, pose) === null) return null;

  const standing = tileKey(pose.face, pose.u, pose.v);
  const forward = DIR_UV[pose.dir];
  for (const turn of [turnRight, turnLeft]) {
    const toDir = turn(pose).dir;
    const side = DIR_UV[toDir];
    const exit = sideCentre(pose, forward, side);
    // A turn is a half-cell sweep sideways, so the tile it exits into has to be
    // on this same face: the original refuses to turn where it cannot walk.
    if (!onFace(exit.u, exit.v)) continue;
    const key = tileKey(pose.face, exit.u, exit.v);
    if (isBlocked(world, bug, { face: pose.face, u: exit.u, v: exit.v, dir: toDir }, standing)) {
      continue;
    }
    // Reserve both tiles the arc needs for its whole sweep; if either is gone
    // by now, try the other side rather than committing to half a turn.
    const ahead = advance(pose, 0.5);
    const aheadKey = tileKey(ahead.face, ahead.u, ahead.v);
    if (!world.occupancy.reclaim(bug.id, [aheadKey, key])) continue;
    return {
      face: pose.face,
      pivot: [pose.u + side[0] * 0.5, pose.v + side[1] * 0.5],
      s: [-side[0] * 0.5, -side[1] * 0.5],
      e: [forward[0] * 0.5, forward[1] * 0.5],
      toDir,
      angle: 0,
    };
  }
  return null;
}

/** Face-local point and tangent of an arc at its current angle. */
function arcPoint(arc: TurnArc): { u: number; v: number; du: number; dv: number } {
  const c = Math.cos(arc.angle);
  const s = Math.sin(arc.angle);
  return {
    u: arc.pivot[0] + arc.s[0] * c + arc.e[0] * s,
    v: arc.pivot[1] + arc.s[1] * c + arc.e[1] * s,
    du: -arc.s[0] * s + arc.e[0] * c,
    dv: -arc.s[1] * s + arc.e[1] * c,
  };
}

/** Sweep an in-progress turn; the arc's tiles are already reserved. */
function stepTurn(bug: Bug, arc: TurnArc, dt: number, maxSpeed: number): void {
  bug.blockedFor = 0; // sweeping through a turn is progress, not obstruction
  bug.speed = approach(bug.speed, maxSpeed, ACCELERATION * dt);
  arc.angle = Math.min(QUARTER_TURN, arc.angle + TURN_RADIANS_PER_SPEED * bug.speed * dt);
  const at = arcPoint(arc);
  const done = arc.angle >= QUARTER_TURN;
  bug.pose = { face: arc.face, u: at.u, v: at.v, dir: done ? arc.toDir : bug.pose.dir };
  if (done) bug.motion = { kind: "cruising" };
}

/**
 * One step of walking: ramp the speed toward whatever the way ahead allows,
 * then spend it, stopping at every tile boundary crossed to decide whether to
 * turn aside. Splitting the step at boundaries is what keeps walkers on lane
 * centres — a turn may only start where the arc lands on one.
 */
function walk(world: Surroundings, bug: Bug, dt: number, maxSpeed: number): void {
  const reach = distanceToBlocker(world, bug, bug.pose);
  const gap = reach === null ? null : Math.max(0, reach - BOUNDARY_SLACK);
  bug.speed = approach(bug.speed, targetSpeed(maxSpeed, gap), ACCELERATION * dt);

  let budget = Math.min(bug.speed * dt, gap ?? Number.POSITIVE_INFINITY);
  let pose = bug.pose;
  while (budget > EPS) {
    const toBoundary = distanceToNextTile(pose);
    if (toBoundary > budget) {
      pose = advance(pose, budget);
      break;
    }
    pose = advance(pose, toBoundary);
    budget -= toBoundary;
    const arc = planTurn(world, bug, pose);
    if (arc) {
      // The rest of this step's budget goes into the sweep on the next update;
      // at 1/60 s the pause is not visible.
      bug.pose = pose;
      bug.motion = { kind: "turning", arc };
      return;
    }
  }

  // The gap above already guarantees the body stays clear, so this only ever
  // fails if someone claimed a tile mid-step; standing still is then correct.
  if (world.occupancy.reclaim(bug.id, bodyTiles(pose))) bug.pose = pose;
  else bug.speed = 0;

  bug.blockedFor = bug.speed < STOPPED_SPEED ? bug.blockedFor + dt : 0;
}

// ---------------------------------------------------------------------------
// Infection: the decision chain and what each kind actually eats (plan 14)
// ---------------------------------------------------------------------------

/** The grid cell under a pose, as the `Cell` tuple the block model speaks. */
function cellAt(pose: SurfacePose): Cell {
  const c = poseToCell(pose);
  return [c.x, c.y, c.z];
}

/**
 * Whether a spy standing here has found a block worth marking, and mark it.
 * `BugsModel.cs:1355-1374`: every non-empty block it enters gets marked once,
 * with no randomness at all. Returns whether the list changed.
 */
function markBlock(world: Surroundings, bug: Bug): boolean {
  const cell = cellAt(bug.pose);
  if (!world.blocks.isWalkable(cell[0], cell[1], cell[2])) return false;
  for (const m of bug.markedBlocks) {
    if (m[0] === cell[0] && m[1] === cell[1] && m[2] === cell[2]) return false;
  }
  bug.markedBlocks.push(cell);
  return true;
}

/** Drop a spy's marks — a kill forfeits them — and announce it if any existed. */
function forfeitMarks(world: Surroundings, bug: Bug): void {
  if (bug.markedBlocks.length === 0) return;
  bug.markedBlocks.length = 0;
  world.marksChanged(bug);
}

/**
 * Whether the walker stops on the tile it has just entered to eat — the
 * original's infect-decision chain (`BugsModel.cs:1652-1677`). The chain's shape
 * matters as much as its rules:
 *
 * - the spy's test is a plain `if`, **not** an `else if`, so a spy falls through
 *   into the generic branch as well and can be tipped into an early cash-in by
 *   the coin flip with fewer than `SPY_MARKS_TO_INFECT` marks;
 * - **lady and black are excluded outright** — they never infect anything;
 * - the generic branch is a **coin flip re-rolled on every tile entered** once
 *   the distance budget has run out, so a bite is deferred by two blocks on
 *   average rather than taken the instant the counter hits zero;
 * - every branch requires a `healthy` block. The *destroy* step guards on
 *   `!= Empty`, but the decision does not: a bug never stops to chew a ghost.
 *
 * Spike tests the block **ahead** (the original's forward probe at `:1647`)
 * while the generic branch tests the one just entered, which is what makes a
 * spike stop *before* stepping onto healthy ground.
 */
function isBiteDue(world: Surroundings, bug: Bug): boolean {
  const kind = bug.kindDef;
  const healthyAt = (pose: SurfacePose): boolean => {
    const c = cellAt(pose);
    return world.blocks.state(c[0], c[1], c[2]) === "healthy";
  };

  let infect = false;
  if (kind === "spy" && bug.markedBlocks.length >= SPY_MARKS_TO_INFECT) infect = true;
  if (kind === "spike") {
    if (healthyAt(advance(bug.pose, 1))) infect = true;
  } else if (kind !== "lady" && kind !== "black") {
    // `Random.Range(0, 2) == 0` — draw unconditionally so the RNG stream does
    // not depend on the block state, as the original's does not either.
    if (bug.tilesUntilInfect <= 0) {
      const heads = Math.floor(world.rng() * 2) === 0;
      if (heads && healthyAt(bug.pose)) infect = true;
    }
  }
  return infect;
}

/**
 * The fat bug's area bite (`BugsModel.cs:1900-1923`). It walks the `EIGHT_STAR`
 * ring forward from a random start with wraparound and stops as soon as the bite
 * accounts for `FAT_STAR_INFECTS` blocks — a count that starts at 1 for the
 * block underneath, so the star adds **at most two** more.
 *
 * The count increments on any **non-empty** block, while `infect()` itself
 * no-ops on anything but a healthy one: a ghost neighbour therefore uses up one
 * of the two slots without being infected. Faithful to the original, whose
 * `amountInfected++` sits outside `InfectBlock`'s own `Healthy` guard.
 */
function fatStarInfect(world: Surroundings, bug: Bug): void {
  // `Random.Range(0, 7)` is exclusive, so index 7 is never a start. Kept.
  const start = Math.floor(world.rng() * (EIGHT_STAR.length - 1));
  let index = start;
  let accounted = 1;
  do {
    const [forward, left] = EIGHT_STAR[index];
    const cell = cellAt(starOffset(bug.pose, forward, left));
    if (world.blocks.isWalkable(cell[0], cell[1], cell[2])) {
      world.blocks.infect(cell[0], cell[1], cell[2]);
      accounted++;
    }
    index = (index + 1) % EIGHT_STAR.length;
  } while (index !== start && accounted !== FAT_STAR_INFECTS);
}

/**
 * A star offset resolved on the surface: `forward` cells along the heading, then
 * `left` cells along its left perpendicular. Two orthogonal `advance()` calls
 * rather than one vector offset — on a flat face they land on the same point,
 * but only this folds correctly when the star reaches across a cube edge, where
 * "left" is a different world direction after the crossing.
 */
export function starOffset(pose: SurfacePose, forward: number, left: number): SurfacePose {
  const ahead = forward === 0 ? pose : advance(pose, forward);
  return left === 0 ? ahead : advance(turnLeft(ahead), left);
}

/**
 * What a bite actually destroys, once the eating animation has run out.
 * `BugDestroy` (`BugsModel.cs:1869-1929`) splits hard on the spy: it cashes in
 * its marks and — being the `if` half of an `if/else` — **never** touches the
 * block under itself. Every other kind eats what it stands on, and the fat bug
 * then adds its ring.
 */
function takeBite(world: Surroundings, bug: Bug): void {
  if (bug.kindDef === "spy") {
    for (const m of bug.markedBlocks) world.blocks.infect(m[0], m[1], m[2]);
    forfeitMarks(world, bug);
    return;
  }
  const cell = cellAt(bug.pose);
  if (world.blocks.isWalkable(cell[0], cell[1], cell[2])) {
    world.blocks.infect(cell[0], cell[1], cell[2]);
  }
  if (bug.kindDef === "fat") fatStarInfect(world, bug);
}

/** Seconds this bug tolerates being stuck; the id term staggers give-ups. */
function blockedLimit(id: BugId): number {
  return BLOCKED_SECONDS_BASE + (id % 10) / 2;
}

/**
 * World placement of a bug, for the renderer: the surface frame of its pose,
 * or the arc's own point and tangent while it is turning — `pose.dir` still
 * holds the *entry* heading then, so it must not drive the body's facing.
 */
export function bugFrame(bug: Bug): { pos: Vec3; normal: Vec3; forward: Vec3 } {
  if (bug.motion.kind !== "turning") return poseToWorld(bug.pose);
  const arc = bug.motion.arc;
  const at = arcPoint(arc);
  return frameAtHeading(arc.face, at.u, at.v, at.du, at.dv);
}

/**
 * The one place bug transitions live (00-architecture.md §5): an exhaustive
 * switch with a `never` check. Mutates the bug; the caller emits the events.
 */
function updateBug(world: Surroundings, bug: Bug, dt: number): StepResult {
  const state = bug.state;
  // The black bug's own clock (`BugsModel.cs:1239-1249`), checked before the
  // state machine because it applies to running and eating alike. It is a
  // `NotCounting` kill: the full death animation, no score.
  if (bug.kindDef === "black" && isKillable(state) && bug.lifeTime >= BLACK_DESPAWN_SECONDS) {
    bug.state = { kind: "dying", progress: 0 };
    return "changed";
  }
  switch (state.kind) {
    case "spawning": {
      state.progress += dt / SPAWN_SECONDS;
      if (state.progress < 1 - PROGRESS_EPSILON) return "none";
      bug.state = { kind: "running" };
      return "changed";
    }
    case "running": {
      // The bug-freeze power-up: the port of `CubeWalk`'s `if (Frozen) return;`
      // (`BugsModel.cs:2018`), gating exactly what CubeWalk gates — movement.
      // CORRECTION: a per-step condition rather than the original's flag
      // stamped by `CheckBugsFreeze`, so a bug spawned mid-freeze freezes too
      // (see `DIVERGENCES.md`). `affectedByFreeze` is true for every kind but
      // black — the reason `BUG_DEFS` has carried it since plan 04. Life time
      // still accrues (a frozen bug tapped young still scores its multiplier);
      // `blockedFor` does not — frozen is not stuck, and the give-up cull must
      // not fire from it.
      if (world.modifiers.frozen && BUG_DEFS[bug.kindDef].affectedByFreeze) {
        bug.lifeTime += dt;
        return "none";
      }
      // The one place the level's speed ramp enters bug movement — see
      // `Surroundings.speedFactor` for why it scales the top speed rather than
      // the per-frame travel the original scales.
      const maxSpeed = BUG_DEFS[bug.kindDef].speed * world.speedFactor;
      const before = tileKey(bug.pose.face, bug.pose.u, bug.pose.v);
      if (bug.motion.kind === "turning") stepTurn(bug, bug.motion.arc, dt, maxSpeed);
      else walk(world, bug, dt, maxSpeed);
      bug.lifeTime += dt;
      // Comparing the tile before and after the step catches every entry —
      // a boundary crossed mid-walk and one an arc sweeps over alike — and can
      // never double-count a walker resting on a boundary. Everything the
      // original does "on entering a new block" hangs off this one test.
      if (tileKey(bug.pose.face, bug.pose.u, bug.pose.v) !== before) {
        // Bugs do not avoid power-ups — they destroy them: the original's
        // `HandleOccupationOnEnteredBlock` flips one on the entered block to
        // `Killed` (`BugsModel.cs:1752-1760`), and this tile-entry hook is the
        // exact same moment.
        if (world.powerups !== null) world.powerups.killAt(cellAt(bug.pose));
        bug.tilesUntilInfect--;
        // Marking runs *before* the decision — "other way round doesnt work",
        // as the comment at `BugsModel.cs:1357` puts it: the block just entered
        // has to be able to be the third mark that triggers the cash-in.
        if (bug.kindDef === "spy" && markBlock(world, bug)) world.marksChanged(bug);
        if (isBiteDue(world, bug)) {
          bug.state = { kind: "infecting", progress: 0 };
          bug.speed = 0; // it comes to a halt to eat, and ramps up again after
          return "changed";
        }
      }
      // Walkers that can neither pass nor turn aside give up, as in the
      // original — otherwise two of them face off on the same lane forever.
      // `KillBug` clears a spy's marks whatever killed it, this cull included.
      if (bug.blockedFor >= blockedLimit(bug.id)) {
        forfeitMarks(world, bug);
        bug.state = { kind: "dying", progress: 0 };
        return "changed";
      }
      return "none";
    }
    case "infecting": {
      // Stationary while eating, keeping its tile claims, then the kind's own
      // bite — the original's `BugDestroy` (`BugsModel.cs:1869-1929`).
      bug.lifeTime += dt;
      state.progress += dt / BITE_SECONDS;
      if (state.progress < 1 - PROGRESS_EPSILON) return "none";
      takeBite(world, bug);
      bug.tilesUntilInfect = world.nextBiteDistance();
      bug.state = { kind: "running" };
      return "changed";
    }
    case "dying":
      state.progress += dt / DIE_SECONDS;
      return state.progress < 1 - PROGRESS_EPSILON ? "none" : "removed";
    case "takenByPlayer":
      // Picked up rather than killed: the same shape as `dying`, on the `pick`
      // clip's own duration.
      state.progress += dt / PICK_SECONDS;
      return state.progress < 1 - PROGRESS_EPSILON ? "none" : "removed";
    default: {
      const unreachable: never = state;
      throw new Error(`unhandled bug state ${JSON.stringify(unreachable)}`);
    }
  }
}

/** States a player hit (or any kill) can act on. */
export function isKillable(state: BugState): boolean {
  return state.kind === "running" || state.kind === "infecting";
}

/**
 * Score multiplier for how long the bug had been alive — `unity-overview.md` §5
 * "Score multipliers" (`BugLifeTimeIndicators`, applied in `KillBug`).
 */
function lifeTimeMultiplier(lifeTime: number): number {
  for (const step of LIFETIME_MULTIPLIERS) if (lifeTime < step.under) return step.multiplier;
  return 1;
}

/**
 * The lifecycle a fatal tap sends a bug into. The lady is *taken* rather than
 * killed — the original's `KillBug(..., killedByPlayer: true)` puts Lady and
 * Black into `TakenByPlayer`, which plays their `pick` clip.
 *
 * TODO(black): the black bug belongs on the same branch, but tapping it ends
 * the game (`ConductGameOver`) and scores nothing. Until game over exists it
 * simply dies like the rest, which keeps it out of the sim's way. Plan 14
 * landed the *other* half of the black bug — its `BLACK_DESPAWN_SECONDS` timer
 * in `updateBug` — so this is all that is left of it.
 */
function fatalState(kind: BugKind): BugState {
  if (kind === "lady") return { kind: "takenByPlayer", progress: 0 };
  return { kind: "dying", progress: 0 };
}

/**
 * Owns every live bug. Ids are handed out from a counter and the map preserves
 * insertion order, so two managers fed identical calls stay identical
 * (00-architecture.md §3).
 */
export class BugManager {
  private readonly map = new Map<BugId, Bug>();
  private nextId = 1;
  /** Tile claims of every live walker; tests reach in to fence a bug off. */
  readonly occupancy = new Occupancy();
  /**
   * The trample hook, late-bound by `createWorld` — the construction order
   * cannot satisfy both directions (`PowerUpManager` takes this manager for
   * the kill-half effect). Null is a live configuration: suites not about
   * power-ups build a `BugManager` alone and nothing is trampled.
   */
  powerups: PowerUpSink | null = null;

  // `blocks` is read-only from here: walkers avoid holes and bite what they
  // stand on, and the block model emits its own `blockChanged` events.
  // `modifiers` defaults to a fresh record so those same suites stay at 1x/unfrozen.
  constructor(
    private readonly events: EventBus,
    readonly blocks: BlocksModel,
    readonly rng: Rng,
    private readonly levels: LevelSource,
    readonly modifiers: GlobalModifiers = createModifiers(),
  ) {}

  /**
   * `Surroundings.speedFactor` — the level's walker time dilation times the
   * power-up speed effect. The original multiplies the two side by side at
   * every movement site (`BugsModel.cs:2299`, `:703`, `:1054`, `:1956`,
   * `:2444`); plan 15 funnelled them all through this one getter.
   */
  get speedFactor(): number {
    return this.levels.speedFactor * this.modifiers.speedFactor;
  }

  /** Announce a spy's marks after `markedBlocks` was mutated (`Surroundings`). */
  marksChanged(bug: Bug): void {
    // Copied: the list keeps being mutated in place, and a listener that stored
    // the payload would otherwise watch its own snapshot change underneath it.
    this.events.emit("bugMarksChanged", { id: bug.id, marks: [...bug.markedBlocks] });
  }

  /**
   * Tiles a walker covers before its next bite — the original's
   * `DistanceLeft = Random.Range(1, 20)` (`BugsModel.cs:1927`). Unity's integer
   * overload has an exclusive upper bound, so this draws 1..19.
   */
  nextBiteDistance(): number {
    return INFECT_TILES_MIN + Math.floor(this.rng() * (INFECT_TILES_MAX - INFECT_TILES_MIN));
  }

  get bugs(): ReadonlyMap<BugId, Bug> {
    return this.map;
  }

  /** Add a bug in `spawning` at `pose`; it starts moving after SPAWN_SECONDS. */
  spawn(kind: BugKind, pose: SurfacePose): Bug {
    const id = this.nextId++ as BugId;
    const bug: Bug = {
      id,
      kindDef: kind,
      pose,
      state: { kind: "spawning", progress: 0 },
      motion: { kind: "cruising" },
      speed: 0, // the original spawns at a standstill and accelerates away
      lifeTime: 0,
      blockedFor: 0,
      tilesUntilInfect: this.nextBiteDistance(),
      hitsTaken: 0,
      markedBlocks: [],
    };
    this.map.set(id, bug);
    // A spawning bug is not moving yet, so it reserves only the tile it stands
    // on — that keeps a spawn from being blocked by a neighbour and lets the
    // walker take its leading tile on its first running step.
    this.occupancy.reclaim(id, restingTile(pose));
    this.events.emit("bugSpawned", { id, kind });
    return bug;
  }

  /**
   * A player tap — the port of `BugsModel.HurtBug`. The fat bug absorbs
   * `FAT_HITS` taps and only announces the non-fatal ones; every other kind
   * dies to the first. The fatal tap scores the kind's points at the life-time
   * multiplier, which is what a killing-spree tracker will later chain onto
   * (`world.time` is queryable, so this event carries all it needs).
   */
  hurt(id: BugId): void {
    const bug = this.map.get(id);
    if (!bug || !isKillable(bug.state)) return;
    bug.hitsTaken++;
    if (bug.hitsTaken < (bug.kindDef === "fat" ? FAT_HITS : 1)) {
      this.events.emit("bugHit", { id, hitsTaken: bug.hitsTaken });
      return;
    }
    // `KillBug` clears the marks before it scores and without consulting
    // `killedByPlayer` (`BugsModel.cs:2804`): a spy tapped one mark short of
    // cashing in destroys nothing at all.
    forfeitMarks(this, bug);
    bug.state = fatalState(bug.kindDef);
    this.events.emit("bugStateChanged", { id, state: bug.state.kind });
    this.events.emit("scored", {
      points: BUG_DEFS[bug.kindDef].points,
      multiplier: lifeTimeMultiplier(bug.lifeTime),
    });
  }

  /** Send a running/infecting bug into its death animation; otherwise a no-op. */
  kill(id: BugId): void {
    const bug = this.map.get(id);
    if (!bug || !isKillable(bug.state)) return;
    forfeitMarks(this, bug);
    bug.state = { kind: "dying", progress: 0 };
    this.events.emit("bugStateChanged", { id, state: "dying" });
  }

  /** Advance every bug by `dt` seconds, emitting transitions and removals. */
  update(dt: number): void {
    // Deleting the current entry while iterating a Map is well defined, so the
    // dying bug can be dropped in place without copying the collection.
    for (const bug of this.map.values()) {
      const result = updateBug(this, bug, dt);
      if (result === "changed") {
        this.events.emit("bugStateChanged", { id: bug.id, state: bug.state.kind });
      } else if (result === "removed") {
        this.map.delete(bug.id);
        this.occupancy.releaseAll(bug.id);
        this.events.emit("bugRemoved", { id: bug.id });
      }
    }
  }
}
